import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { fetchCategories } from '@/api/library';
import type { HubCategory } from '@/types';
import { Button } from '@/components/ui/Button';
import { CategoryPanel } from '@/components/home/CategoryPanel';
import { brandLang, useI18n } from '@/i18n';
import { categoryLabel } from '@/lib/categories';

const FALLBACK_CATEGORIES: HubCategory[] = [
  { id: 'live', label: 'Live' },
  { id: 'slot', label: 'Slot' },
  { id: 'instant', label: 'Instant', comingSoon: true },
];

export function HomePage() {
  const { accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();
  const [categories, setCategories] = useState<HubCategory[]>(FALLBACK_CATEGORIES);
  /** Nothing is open at rest; only pointer or keyboard focus opens a panel. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [heroReady, setHeroReady] = useState(false);

  useEffect(() => {
    const timer = requestAnimationFrame(() => setHeroReady(true));
    return () => cancelAnimationFrame(timer);
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    fetchCategories(accessToken, sessionId)
      .then((items) => {
        if (items.length === 0) return;
        setCategories(items);
        setOpenId((current) => (items.some((item) => item.id === current) ? current : null));
      })
      .catch(() => {
        /* keep fallback */
      });
  }, [accessToken, sessionId, locale]);

  return (
    <section className="home-page">
      <div className="home-hero site-pad-x">
        <div className="home-hero__media" aria-hidden="true">
          <video
            className="home-hero__video"
            src="/banners/movie1.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
          <span className="home-hero__media-scrim" />
        </div>
        <p className="eyebrow home-hero__eyebrow reveal is-in">
          RICHYPLAY {t('home.eyebrow')}
        </p>
        <h1
          className={`hero-title-anim live-hero-title display${heroReady ? ' is-in' : ''}`}
          lang={brandLang(t('home.title'))}
        >
          {t('home.title')}
        </h1>
        <p className="home-hero__lead reveal is-in reveal-delay-1">{t('home.lead')}</p>
        <div className="home-hero__actions reveal is-in reveal-delay-2">
          <Link to="/roadmap">
            <Button variant="glass" className="home-cta">
              {t('home.viewRoadmap')}
            </Button>
          </Link>
        </div>
        <div className="hero-split" aria-hidden="true" />
      </div>

      <div className="home-categories site-pad-x reveal is-in reveal-delay-3">
        <h2 className="section-title">{t('home.browse')}</h2>
        <div className="cat-rail" onPointerLeave={() => setOpenId(null)}>
          {categories.map((cat) => (
            <CategoryPanel
              key={cat.id}
              category={cat}
              label={categoryLabel(t, cat)}
              cta={t('home.openLibrary')}
              soonLabel={t('nav.comingSoon')}
              open={cat.id === openId}
              onOpen={() => setOpenId(cat.id)}
              onClose={() => setOpenId((current) => (current === cat.id ? null : current))}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
